import { WatchQueryFetchPolicy } from '@apollo/client';
import { useEffect } from 'react';

import {
  Team,
  PublicTeam,
  TeamOrPublicTeam,
  useGetPublicTeamQuery,
} from '@graphql/generated';
import { SESSION_SELECTED_TEAM_NAME } from '@src/constants/constants';
// TA-10850: the same not-found verdict the errorLink and the team screens
// use. The old `message.includes('could not be found')` check could not see
// the HTTP-401 form of the answer, whose top-level message is only
// "Response not successful: Received status code 401".
import { isApolloResourceNotFound } from '@utils/isResourceNotFoundError';

import { useApolloClient } from './useApolloClient';
/**
 * Retrieves public team information for non-members
 */
export const usePublicTeamFromQuery = ({
  teamUUId,
  fetchPolicy = 'cache-and-network',
  shouldClearSelectedTeamOnNotFound = false,
}: {
  teamUUId?: Team['uuid'] | PublicTeam['uuid'];
  fetchPolicy?: WatchQueryFetchPolicy;
  /**
   * Clear the persisted selected-team name when this team turns out not to
   * exist. Opt-in, and off by default — the same rule useTeamDetailFromQuery
   * follows: this is a generic reader, and most of its call sites (invitation
   * previews, member pickers, subscription stacks) look up a team they do not
   * own the drawer selection for. A stale uuid there must not blank out the
   * selection that belongs to another route.
   *
   * The stale cache entry is evicted either way: that is local to the record
   * this hook just failed to read, not global navigation state.
   */
  shouldClearSelectedTeamOnNotFound?: boolean;
}) => {
  const { client } = useApolloClient();

  const {
    data: { getPublicTeam } = {},
    loading,
    error,
    refetch,
  } = useGetPublicTeamQuery({
    variables: {
      id: teamUUId || '',
    },
    fetchPolicy,
    skip: !teamUUId,
  });

  useEffect(() => {
    if (isApolloResourceNotFound(error)) {
      if (
        shouldClearSelectedTeamOnNotFound &&
        typeof window !== 'undefined' &&
        window.sessionStorage
      ) {
        window.sessionStorage.removeItem(SESSION_SELECTED_TEAM_NAME);
      }

      if (client && teamUUId) {
        const cacheId = client.cache.identify({
          __typename: 'PublicTeam',
          uuid: teamUUId,
        });
        if (cacheId) {
          client.cache.evict({ id: cacheId });
          client.cache.gc();
        }
      }
    }
  }, [error, teamUUId, client, shouldClearSelectedTeamOnNotFound]);

  // Extract team data from union type
  let team: Team | PublicTeam | undefined;
  if (getPublicTeam?.__typename === 'Team') {
    team = getPublicTeam as Team;
  } else if (getPublicTeam?.__typename === 'PublicTeam') {
    team = getPublicTeam as PublicTeam;
  } else {
    team = undefined;
  }

  // TA-10772: whether *this* user already asked to join. It is the only
  // signal the API exposes for that — listPendingUserRequests is the admin's
  // approval queue and a non-member cannot read it. Both branches of the
  // union carry the field, so read it here once instead of making every call
  // site narrow to PublicTeam and silently lose the flag on the Team branch.
  const hasPendingMemberAdditionRequest =
    team?.hasPendingMemberAdditionRequest ?? false;

  return {
    team: team as TeamOrPublicTeam | undefined,
    publicTeam:
      team?.__typename === 'PublicTeam' ? (team as PublicTeam) : undefined,
    fullTeam: team?.__typename === 'Team' ? (team as Team) : undefined,
    hasPendingMemberAdditionRequest,
    loading,
    error,
    refetch,
  };
};
